import { Fragment } from "react";
import { Label, Listbox, ListboxOption, ListboxOptions, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";

import classNames from "../../../utils/classNames";

interface DotOption {
  id: any;
  name: string;
  dotClassName?: string;
}

interface SelectInputWithDotProps {
  label: string;
  register: any;
  registerName: string;
  options: DotOption[];
  setValue: any;
  watch: any;
  cols?: string;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  rounded?: boolean;
  labelPosition?: "top" | "inline";
  placeholder?: string;
  classes?: string;
}

export default function SelectInputWithDot({
  label,
  register,
  registerName,
  options,
  setValue,
  watch,
  cols = "sm:col-span-1",
  disabled = false,
  size = "md",
  rounded = false,
  labelPosition = "top",
  placeholder = "Select an option",
  classes = "",
}: SelectInputWithDotProps) {
  const selectValue = watch(registerName);
  const selectedOption = options.find((e) => e.id === selectValue);

  const sizeClasses = {
    sm: "px-2 py-1 text-sm",
    md: "px-3 py-2 text-sm",
    lg: "px-4 py-3 text-base",
  };

  return (
    <div className={cols}>
      <Listbox
        disabled={disabled}
        {...register(registerName)}
        value={selectValue}
        onChange={(e) => setValue(registerName, e)}
      >
        {({ open }) => (
          <>
            <div
              className={classNames(
                "w-full",
                labelPosition === "inline" ? "flex items-center gap-x-2" : ""
              )}
            >
              <Label
                className={classNames(
                  "text-sm font-medium text-gray-500",
                  labelPosition === "top" ? "block mb-2" : "shrink-0"
                )}
              >
                {label}
              </Label>

              <div className="w-full relative">
                <Listbox.Button
                  className={classNames(
                    "relative w-full text-left cursor-pointer border bg-white border-1 border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500",
                    sizeClasses[size],
                    rounded ? "rounded-md" : "",
                    classes
                  )}
                >
                  <span className="block truncate">
                    <span className={classNames(!selectedOption?.name ? "text-gray-400" : "", "flex items-center gap-2")}>
                      <span
                        className={classNames(
                          selectedOption?.dotClassName ?? "bg-gray-300",
                          "inline-block h-2 w-2 shrink-0 rounded-full"
                        )}
                      />
                      <span>{selectedOption?.name ?? placeholder}</span>
                    </span>
                  </span>

                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-700" aria-hidden="true" />
                  </span>
                </Listbox.Button>

                <Transition
                  show={open}
                  appear={true}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute z-20 text-sm mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto">
                    {options.map((e) => (
                      <ListboxOption
                        key={e.id}
                        className={({ active }) =>
                          classNames(
                            active ? "bg-primary-100 text-gray-900" : "text-gray-900",
                            "cursor-default select-none relative py-2 pl-8 pr-4"
                          )
                        }
                        value={e.id}
                      >
                        <>
                          <span
                            className={classNames(
                              selectValue === e.id ? "font-semibold" : "font-normal",
                              "flex items-center gap-2 truncate"
                            )}
                          >
                            <span
                              className={classNames(
                                e.dotClassName ?? "bg-gray-300",
                                "inline-block h-2 w-2 shrink-0 rounded-full"
                              )}
                            />
                            <span>{e.name}</span>
                          </span>

                          {selectValue === e.id ? (
                            <span className="text-primary-600 absolute inset-y-0 left-0 flex items-center pl-1.5">
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </div>
            </div>
          </>
        )}
      </Listbox>
    </div>
  );
}
